import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Upload, X, Video, AlertCircle, Check } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { 
  uploadVideoMultipart, 
  generateVideoThumbnail,
  uploadThumbnail 
} from "@/utils/multipartVideoUpload";

interface PropertyVideoUploadProps {
  propertyId?: string;
  currentVideoUrl?: string | null;
  onVideoUploaded: (videoUrl: string) => void;
  onVideoRemoved: () => void;
}

export const PropertyVideoUpload = ({
  propertyId,
  currentVideoUrl,
  onVideoUploaded,
  onVideoRemoved
}: PropertyVideoUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB
  const ACCEPTED_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file type
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Invalid video format. Please upload MP4, MOV, AVI, or WEBM.');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError('Video file is too large. Maximum size is 1GB.');
      return;
    }

    if (!propertyId) {
      setError('Property ID is required to upload video.');
      return;
    }

    // Immediately read file data into memory to prevent stale references
    // This is critical for Android WebView which can invalidate file handles
    // when the app goes to background or during memory pressure
    try {
      setIsUploading(true);
      setUploadStatus('Reading video file...');
      setUploadProgress(0);
      
      const arrayBuffer = await file.arrayBuffer();
      
      // Create fresh File object from ArrayBuffer to ensure valid handle
      const freshFile = new File([arrayBuffer], file.name, { type: file.type });
      
      await uploadVideo(freshFile);
    } catch (readError: any) {
      console.error('Video file read error:', readError);
      setError('Failed to read video file. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  const uploadVideo = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus('Preparing upload...');
    setError(null);

    try {
      // Generate thumbnail first
      let thumbnailBlob: Blob | null = null;
      try {
        setUploadStatus('Generating thumbnail...');
        thumbnailBlob = await generateVideoThumbnail(file);
      } catch (thumbnailError) {
        console.warn('Failed to generate thumbnail:', thumbnailError);
        // Continue without thumbnail
      }

      // Upload video using multipart upload
      const result = await uploadVideoMultipart(
        file,
        propertyId!,
        (progress, status) => {
          setUploadProgress(progress);
          setUploadStatus(status);
        }
      );

      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      // Update property with video URL
      const updateData: Record<string, string | null> = { 
        video_url: result.videoUrl! 
      };

      // Upload thumbnail separately if we have one
      if (thumbnailBlob) {
        try {
          setUploadStatus('Uploading thumbnail...');
          const thumbnailUrl = await uploadThumbnail(thumbnailBlob, propertyId!);
          if (thumbnailUrl) {
            updateData.video_thumbnail_url = thumbnailUrl;
          }
        } catch (thumbError) {
          console.warn('Thumbnail upload failed:', thumbError);
        }
      }

      const { error: updateError } = await supabase
        .from('properties')
        .update(updateData)
        .eq('id', propertyId!);

      if (updateError) throw updateError;

      onVideoUploaded(result.videoUrl!);
      
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadStatus('');
      }, 1000);

    } catch (err: any) {
      console.error('Video upload error:', err);
      setError(err.message || 'Failed to upload video. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  const handleRemoveVideo = async () => {
    if (!currentVideoUrl) return;

    try {
      // Delete from R2 (function will handle both video and thumbnail)
      await supabase.functions.invoke('delete-video-from-r2', {
        body: { videoUrl: currentVideoUrl }
      });

      // Update property - clear both video_url and video_thumbnail_url
      const { error } = await supabase
        .from('properties')
        .update({ 
          video_url: null,
          video_thumbnail_url: null 
        } as any)
        .eq('id', propertyId!);

      if (error) throw error;

      onVideoRemoved();
    } catch (err: any) {
      console.error('Video removal error:', err);
      setError(err.message || 'Failed to remove video');
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Property Video</h3>
          <p className="text-sm text-muted-foreground">
            Add a video tour of the property (Max 1GB, MP4/MOV/WEBM)
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {currentVideoUrl ? (
        <div className="space-y-4">
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <video 
              controls
              playsInline
              // @ts-ignore - webkit-playsinline is a non-standard attribute for legacy iOS
              webkit-playsinline=""
              className="w-full h-full"
              preload="metadata"
            >
              <source src={currentVideoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                Replace Video
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={isUploading}>
                    <X className="h-4 w-4 mr-2" />
                    Remove Video
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove Property Video?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. The video will be permanently deleted 
                      from your property listing.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleRemoveVideo}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Yes, Remove Video
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Desktop: Save button inline */}
              <Button 
                className="hidden sm:flex bg-godyspo-gold text-black hover:bg-godyspo-gold/90"
                onClick={() => navigate(`/seller/properties/${propertyId}`)}
                disabled={isUploading}
              >
                <Check className="h-4 w-4 mr-2" />
                Save Video
              </Button>
            </div>
            
            {/* Mobile: Save button full-width below */}
            <Button 
              className="sm:hidden w-full bg-godyspo-gold text-black hover:bg-godyspo-gold/90"
              onClick={() => navigate(`/seller/properties/${propertyId}`)}
              disabled={isUploading}
            >
              <Check className="h-4 w-4 mr-2" />
              Save Video
            </Button>
          </div>
        </div>
      ) : (
        <div>
          {isUploading ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{uploadStatus}</span>
                <span className="font-medium">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-xs text-muted-foreground text-center">
                Please keep this page open while uploading
              </p>
            </div>
          ) : (
            <div
              className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-godyspo-gold transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Video className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">Upload Property Video</p>
              <p className="text-sm text-muted-foreground mb-4">
                Click to browse or drag and drop
              </p>
              <p className="text-xs text-muted-foreground">
                Supported formats: MP4, MOV, AVI, WEBM (Max 1GB)
              </p>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
        onChange={handleFileSelect}
        className="hidden"
      />
    </Card>
  );
};
